/**
 * SectionLayoutSettings — Inline settings panel for FlexibleLayout section layout properties.
 *
 * Renders inside a Collapse below each compact section row in V2FlexibleSectionsSection.
 * Follows the V2ListStyleSections accordion pattern: nested MUI Accordions (not SAccordion)
 * with 2-column grid fields and PaddingElement for EdgeInsets.
 *
 * Architecture:
 * - Reads values from section.layout via getValueByPath
 * - Writes via onLayoutChange(updatedLayout) — parent handles atomic sections array write
 * - Supports SECTION_KIND_SETTINGS overrides (hidden/forced fields)
 * - Translates PaddingElement paths (paddingTop/Right/Bottom/Left → top/end/bottom/start)
 */

import React, { useMemo, useCallback } from 'react';
import {
  Box,
  Chip,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { LayoutSection, SectionKind, SectionLayout } from '@segmently/schema-models/v2';
import { SAccordionNestingContext } from '@modules/ui-components';
import { PaddingElement, type PaddingValues } from '../../../factories/elements';
import { FlexibleMediaSection } from './FlexibleMediaSection';
import { StyleFieldRow } from '../../shared/styleFields/StyleFieldRow';
import type { FieldConfig } from '../../../types/editorFieldTypes';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';
import { FIELD_STYLE_GRID_GAP } from '../../../config/ui';
import { EXPANDED_SECTION_BGCOLOR } from '../../../config/editorFieldStyles';
import {
  getValueByPath,
  setNestedValue,
  deleteNestedValue,
  pruneEmptyPresentationOverrides,
} from '../../../utils/v2FieldOperations';
import {
  createSectionLayoutFieldGroups,
  getHeightValueOptions,
  getWidthValueOptions,
  SECTION_KIND_SETTINGS,
} from '../../../config/sectionLayoutSettingsConfig';
import { useEditorGuideService } from '@modules/reactFlow/contexts';
import { useEditorPresentationProfile } from './PresentationProfileContext';

// ── Presentation override eligibility (v2.19.0) ─────────────────────────────
// Fields that support per-profile presentationOverrides. When the editor is in
// a non-base profile mode, writes to these layout paths are redirected to
// `presentationOverrides.{profile}.{path}`; everything else stays base-only.

const OVERRIDE_ELIGIBLE_EXACT = new Set([
  'heightKind',
  'heightValue',
  'widthKind',
  'widthValue',
  'maxWidth',
  'minWidth',
  'sectionHorizontalAlignment',
]);

function isOverrideEligible(layoutPath: string): boolean {
  return (
    OVERRIDE_ELIGIBLE_EXACT.has(layoutPath) ||
    layoutPath.startsWith('padding.') ||
    layoutPath.startsWith('margin.')
  );
}

// ── Props ────────────────────────────────────────────────────────────────────

export interface SectionLayoutSettingsProps {
  /** The section whose layout is being edited */
  section: LayoutSection;
  /** Callback with the updated layout object — parent writes the sections array atomically */
  onLayoutChange: (updatedLayout: SectionLayout) => void;
  /** Disables all inputs while saving */
  disabled?: boolean;
  /** Current editing locale (for media upload) */
  locale?: string;
  /** Project ID (for media upload) */
  projectId?: string;
  /** Onboarding ID (for media upload) */
  onboardingId?: string;
  /** Version ID (for media upload) */
  versionId?: string;
  /** Available locales for multi-locale upload dialog */
  availableLocales?: string[];
  /**
   * Screen-level defaultSectionLayout (v2.19.0), if the screen defines one.
   * Only used to show the "Inherits screen default" hint when this section
   * has no own width/padding/margin values.
   */
  screenDefaultLayout?: SectionLayout;
}

// ── Padding path translation (same pattern as V2ListStyleSections) ──────────

/**
 * Translate PaddingElement output paths to EdgeInsets paths.
 * PaddingElement emits: basePath.paddingTop → we need: basePath.top
 */
function translatePaddingPath(path: string): string {
  return path
    .replace('.paddingTop', '.top')
    .replace('.paddingRight', '.end')
    .replace('.paddingBottom', '.bottom')
    .replace('.paddingLeft', '.start');
}

/**
 * Read EdgeInsets from section.layout.padding and map to PaddingValues format.
 * EdgeInsets: { top, end, bottom, start } → PaddingValues: { paddingTop, paddingRight, paddingBottom, paddingLeft }
 */
/**
 * Merge sparse per-profile inset overrides over base EdgeInsets (v2.19.0).
 * Returns base as-is in base mode or when no override block exists.
 */
function getEffectiveInsets(
  section: LayoutSection,
  key: 'padding' | 'margin',
  profile: string,
): Record<string, unknown> | undefined {
  const base = section.layout?.[key] as Record<string, unknown> | undefined;
  if (profile === 'base') return base;
  const overrides = (section.layout as unknown as Record<string, unknown> | undefined)
    ?.presentationOverrides as Record<string, Record<string, unknown>> | undefined;
  const profileInsets = overrides?.[profile]?.[key] as Record<string, unknown> | undefined;
  if (!profileInsets) return base;
  const merged: Record<string, unknown> = { ...base };
  for (const [edge, value] of Object.entries(profileInsets)) {
    if (value !== undefined) merged[edge] = value;
  }
  return merged;
}

function getPaddingValues(section: LayoutSection, profile: string = 'base'): PaddingValues {
  const insets = getEffectiveInsets(section, 'padding', profile);
  return {
    paddingTop: typeof insets?.top === 'number' ? insets.top : 0,
    paddingRight: typeof insets?.end === 'number' ? insets.end : 0,
    paddingBottom: typeof insets?.bottom === 'number' ? insets.bottom : 0,
    paddingLeft: typeof insets?.start === 'number' ? insets.start : 0,
  };
}

/**
 * Read EdgeInsets from section.layout.margin and map to PaddingValues format.
 * Same structure as padding but values can be negative.
 */
function getMarginValues(section: LayoutSection, profile: string = 'base'): PaddingValues {
  const insets = getEffectiveInsets(section, 'margin', profile);
  return {
    paddingTop: typeof insets?.top === 'number' ? insets.top : 0,
    paddingRight: typeof insets?.end === 'number' ? insets.end : 0,
    paddingBottom: typeof insets?.bottom === 'number' ? insets.bottom : 0,
    paddingLeft: typeof insets?.start === 'number' ? insets.start : 0,
  };
}

// ── Component ────────────────────────────────────────────────────────────────

export const SectionLayoutSettings: React.FC<SectionLayoutSettingsProps> = ({
  section,
  onLayoutChange,
  disabled = false,
  locale,
  projectId,
  onboardingId,
  versionId,
  availableLocales,
  screenDefaultLayout,
}) => {
  const theme = useTheme();
  const guideService = useEditorGuideService();
  const fieldGroups = useMemo(() => createSectionLayoutFieldGroups(), []);
  const kindSettings = SECTION_KIND_SETTINGS[section.kind as SectionKind];

  // Active presentation profile (v2.19.0): 'base' = classic behavior;
  // phone/tablet/laptop = eligible fields read/write presentationOverrides.{profile}.*
  const activeProfile = useEditorPresentationProfile();

  // Cast section to Record for getValueByPath compatibility (used by readers below)
  const sectionAsRecord = section as unknown as Record<string, unknown>;

  /** Override value for the active profile, or undefined when not overridden. */
  const readOverrideValue = useCallback(
    (layoutPath: string): unknown => {
      if (activeProfile === 'base' || !isOverrideEligible(layoutPath)) return undefined;
      return getValueByPath(
        sectionAsRecord,
        `layout.presentationOverrides.${activeProfile}.${layoutPath}`,
      );
    },
    [activeProfile, sectionAsRecord],
  );

  /** Effective value shown in a field: profile override first, then base. */
  const readFieldValue = useCallback(
    (fieldPath: string): unknown => {
      const layoutPath = fieldPath.replace(/^layout\./, '');
      const overrideValue = readOverrideValue(layoutPath);
      if (overrideValue !== undefined) return overrideValue;
      return getValueByPath(sectionAsRecord, fieldPath);
    },
    [readOverrideValue, sectionAsRecord],
  );

  // Current heightKind for conditional field rendering (effective for profile mode)
  const currentHeightKind = (readFieldValue('layout.heightKind') as string | undefined)
    ?? section.layout?.heightKind ?? 'auto';

  // Current widthKind (v2.19.0) for conditional widthValue rendering
  const currentWidthKind = (readFieldValue('layout.widthKind') as string | undefined) ?? 'auto';

  // Current background kind for conditional color/media field rendering
  const currentBackgroundKind = section.layout?.background?.kind;

  const effectiveLocale = locale ?? 'en';

  // ── Core field change handler ────────────────────────────────────────

  const handleFieldChange = useCallback(
    (fieldPath: string, value: unknown) => {
      // Strip 'layout.' prefix to get the path within layout object
      const layoutPath = fieldPath.replace(/^layout\./, '');

      // Deep-clone the layout to avoid mutating the original
      const layoutCopy: Record<string, unknown> = JSON.parse(
        JSON.stringify(section.layout ?? {}),
      );

      // Handle boolean conversion for scrollable field
      let resolvedValue = value;
      if (layoutPath === 'scrollable') {
        resolvedValue = value === 'true' || value === true;
      }

      // When changing background.kind, restructure the background object
      if (layoutPath === 'background.kind') {
        if (!resolvedValue) {
          // Clearing background kind → remove entire background
          delete layoutCopy.background;
        } else if (resolvedValue === 'solid') {
          layoutCopy.background = { kind: 'solid', color: (layoutCopy.background as Record<string, unknown>)?.color };
        } else if (resolvedValue === 'media') {
          const existingMedia = (layoutCopy.background as Record<string, unknown>)?.media;
          layoutCopy.background = {
            kind: 'media',
            media: existingMedia ?? {
              kind: 'Image',
              content: { image: { translations: {} } },
              appearance: {},
            },
          };
        }
        // Remove legacy backgroundColor when using new background system
        delete layoutCopy.backgroundColor;
        onLayoutChange(layoutCopy as SectionLayout);
        return;
      }

      // Profile mode (v2.19.0): eligible fields write sparse per-profile
      // overrides instead of base values; base stays intact.
      const effectivePath =
        activeProfile !== 'base' && isOverrideEligible(layoutPath)
          ? `presentationOverrides.${activeProfile}.${layoutPath}`
          : layoutPath;

      // Handle null: delete the property (keeps Firebase data clean)
      if (resolvedValue === null || resolvedValue === undefined) {
        deleteNestedValue(layoutCopy, effectivePath);
        pruneEmptyPresentationOverrides(layoutCopy, effectivePath);
      } else {
        setNestedValue(layoutCopy, effectivePath, resolvedValue);
      }

      // Remove legacy backgroundColor when setting background.color
      if (layoutPath.startsWith('background.')) {
        delete layoutCopy.backgroundColor;
      }

      onLayoutChange(layoutCopy as SectionLayout);
    },
    [section.layout, onLayoutChange, activeProfile],
  );

  // ── Media background proxy (reuses FlexibleMediaSection) ────────────
  // FlexibleMediaSection uses paths like content.featuredMedia.*.
  // We create a proxy step where content.featuredMedia points to
  // section.layout.background.media, and an onChange adapter that maps
  // content.featuredMedia.* → layout.background.media.* for writing.

  const bgMediaProxyStep = useMemo((): FlowStepNode => {
    const media = (section.layout as Record<string, unknown> | undefined)
      ?.background
      ? ((section.layout as Record<string, unknown>).background as Record<string, unknown>)?.media ?? null
      : null;
    return {
      id: `${section.id}-bg-media`,
      content: { featuredMedia: media },
    } as unknown as FlowStepNode;
  }, [section]);

  const handleBgMediaChange = useCallback(
    (path: string, value: unknown) => {
      // Remap: content.featuredMedia → layout.background.media
      const remapped = path.replace(/^content\.featuredMedia/, 'layout.background.media');
      handleFieldChange(remapped, value);
    },
    [handleFieldChange],
  );

  // ── Padding change handler ───────────────────────────────────────────

  const handlePaddingChange = useCallback(
    (path: string, value: number) => {
      const translatedPath = translatePaddingPath(path);
      handleFieldChange(translatedPath, value);
    },
    [handleFieldChange],
  );

  // ── Margin change handler ───────────────────────────────────────────

  const handleMarginChange = useCallback(
    (path: string, value: number) => {
      const translatedPath = translatePaddingPath(path);
      handleFieldChange(translatedPath, value);
    },
    [handleFieldChange],
  );

  // ── Check if a field is overridden by kind settings ──────────────────

  const getFieldOverride = useCallback(
    (fieldPath: string) => {
      if (!kindSettings?.overrides) return undefined;
      // Match by layout property name (strip 'layout.' prefix)
      const layoutKey = fieldPath.replace(/^layout\./, '');
      // Check exact match first, then the top-level property
      return (
        kindSettings.overrides[layoutKey] ??
        kindSettings.overrides[layoutKey.split('.')[0]]
      );
    },
    [kindSettings],
  );

  // ── Filter fields based on visibility rules ──────────────────────────

  const getVisibleFields = useCallback(
    (fields: FieldConfig[]): FieldConfig[] => {
      return fields.filter((field) => {
        // Hide heightValue when heightKind is 'auto'
        if (field.path === 'layout.heightValue' && currentHeightKind === 'auto') {
          return false;
        }

        // Hide widthValue when widthKind is 'auto' or unset (v2.19.0)
        if (field.path === 'layout.widthValue' && (currentWidthKind ?? 'auto') === 'auto') {
          return false;
        }

        // Hide background color when kind is not 'solid' (or undefined)
        if (field.path === 'layout.background.color' && currentBackgroundKind === 'media') {
          return false;
        }

        // Check kind-specific overrides
        const override = getFieldOverride(field.path);
        if (override?.hidden) return false;

        return true;
      });
    },
    [currentHeightKind, currentWidthKind, currentBackgroundKind, getFieldOverride],
  );

  // ── Render ─────────────────────────────────────────────────────────────

  // "Inherits screen default" hint (v2.19.0): shown when the screen defines
  // defaultSectionLayout and this section sets no own width/padding/margin.
  const inheritsScreenDefault =
    !!screenDefaultLayout &&
    Object.keys(screenDefaultLayout).length > 0 &&
    !section.layout?.widthKind &&
    !section.layout?.widthValue &&
    !section.layout?.maxWidth &&
    !section.layout?.minWidth &&
    !section.layout?.sectionHorizontalAlignment &&
    !section.layout?.padding &&
    !section.layout?.margin;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        mb: 2,
      }}
      data-testid="section-layout-settings"
    >
      {/* Section type + label info line */}
      <Typography
        variant="caption"
        sx={{ color: 'text.disabled', fontSize: '0.65rem', px: 1, mb: 0.5 }}
      >
        Container Settings
      </Typography>

      {inheritsScreenDefault && (
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', fontSize: '0.65rem', px: 1, mb: 0.5 }}
          data-testid="section-inherits-screen-default"
        >
          Inherits screen default layout (width / padding / margin)
        </Typography>
      )}

      {fieldGroups.map((group) => {
        const visibleFields = getVisibleFields(group.fields);

        // Skip group if it has no visible fields and no padding/margin config
        if (visibleFields.length === 0 && !group.paddingConfig && !group.marginConfig) return null;

        return (
          <Accordion
            key={group.id}
            defaultExpanded={group.defaultExpanded}
            disableGutters
            elevation={0}
            data-testid={`section-layout-${group.id}`}
            sx={{
              backgroundColor: 'transparent',
              '&:before': { display: 'none' },
              '& .MuiAccordionSummary-root': {
                minHeight: 32,
                px: 1,
                py: 0,
                backgroundColor: theme.palette.action.hover,
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: theme.palette.action.selected,
                },
              },
              '& .MuiAccordionSummary-content': {
                margin: '4px 0',
              },
              '& .MuiAccordionDetails-root': {
                px: 2,
                pt: 1,
                pb: 1,
                bgcolor: EXPANDED_SECTION_BGCOLOR,
                borderRadius: 1,
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
              aria-controls={`section-layout-${group.id}-content`}
              id={`section-layout-${group.id}-header`}
            >
              <Typography variant="caption" fontWeight={500}>
                {group.title}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {/* Render fields in 2-column grid */}
              {visibleFields.length > 0 && (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: FIELD_STYLE_GRID_GAP,
                    mb: (group.paddingConfig || group.marginConfig) ? 1.5 : 0,
                  }}
                  data-testid={`section-layout-${group.id}-fields`}
                >
                  {visibleFields.map((fieldConfig) => {
                    const override = getFieldOverride(fieldConfig.path);
                    const isForced = override?.forcedValue !== undefined;

                    // For heightValue/widthValue: options depend on the current kind
                    let effectiveConfig = fieldConfig;
                    if (fieldConfig.path === 'layout.heightValue') {
                      effectiveConfig = {
                        ...fieldConfig,
                        options: getHeightValueOptions(currentHeightKind),
                      };
                    } else if (fieldConfig.path === 'layout.widthValue') {
                      effectiveConfig = {
                        ...fieldConfig,
                        options: getWidthValueOptions(currentWidthKind),
                      };
                    }

                    const layoutPath = effectiveConfig.path.replace(/^layout\./, '');
                    const profileOverrideValue = readOverrideValue(layoutPath);
                    const hasProfileOverride = profileOverrideValue !== undefined;

                    return (
                      <Box key={effectiveConfig.path} sx={{ position: 'relative' }}>
                        <StyleFieldRow
                          config={effectiveConfig}
                          inputTestId={`layout-${layoutPath.replace(/\./g, '-')}`}
                          value={
                            isForced
                              ? override!.forcedValue
                              : readFieldValue(effectiveConfig.path)
                          }
                          onChange={(value) => {
                            if (isForced) return; // Locked field — ignore changes
                            handleFieldChange(effectiveConfig.path, value);
                          }}
                          disabled={disabled || isForced}
                        />
                        {hasProfileOverride && (
                          <Chip
                            size="small"
                            color="primary"
                            variant="outlined"
                            label={activeProfile.toUpperCase()}
                            onDelete={() => handleFieldChange(effectiveConfig.path, null)}
                            data-testid={`layout-override-badge-${layoutPath.replace(/\./g, '-')}`}
                            sx={{
                              height: 16,
                              fontSize: '0.55rem',
                              mt: 0.25,
                              '& .MuiChip-deleteIcon': { fontSize: 12 },
                            }}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* Render PaddingElement if configured */}
              {group.paddingConfig && (
                <PaddingElement
                  basePath={group.paddingConfig.basePath}
                  values={getPaddingValues(section, activeProfile)}
                  onChange={handlePaddingChange}
                  label={group.paddingConfig.label}
                  guideKey="screenedit-flexible-sections-padding"
                  guideService={guideService ?? undefined}
                  disabled={disabled}
                  defaultExpanded={true}
                  data-testid="flexible-section-layout-padding"
                />
              )}

              {/* Render Margin PaddingElement if configured (supports negative values) */}
              {group.marginConfig && (
                <PaddingElement
                  basePath={group.marginConfig.basePath}
                  values={getMarginValues(section, activeProfile)}
                  onChange={handleMarginChange}
                  label={group.marginConfig.label}
                  guideKey="screenedit-flexible-sections-margin"
                  guideService={guideService ?? undefined}
                  disabled={disabled}
                  defaultExpanded={false}
                  min={-100}
                  data-testid="flexible-section-layout-margin"
                />
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}

      {/* Full media editor for section background (when kind=media).
        * Reuses FlexibleMediaSection via proxy step — remaps
        * content.featuredMedia.* ↔ layout.background.media.* */}
      {currentBackgroundKind === 'media' && (
        <SAccordionNestingContext.Provider value={false}>
          <FlexibleMediaSection
            step={bgMediaProxyStep}
            locale={effectiveLocale}
            onChange={handleBgMediaChange}
            disabled={disabled}
            defaultExpanded={true}
            projectId={projectId}
            onboardingId={onboardingId}
            versionId={versionId}
            availableLocales={availableLocales}
            sectionTitle="Background Media"
          />
        </SAccordionNestingContext.Provider>
      )}
    </Box>
  );
};

export default SectionLayoutSettings;
